/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { CartIconComponent } from '@msdyn365-commerce/components';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { ErrorNotification, NotificationsManager } from '@msdyn365-commerce-modules/notifications-core';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    KeyCodes,
    Modal,
    ModalBody,
    ModalHeader,
    onTelemetryClick,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { HeaderPreferredStore, NavIcon } from './components';
import { IHerbalTopHeaderData } from './herbal-top-header.data';
import { IHerbalTopHeaderProps, IHerbalTopHeaderResources, IMyAccountLinksData } from './herbal-top-header.props.autogenerated';
import { WishListIconComponent } from '../../themes/herbal-adventureworks/views/components/wishlist-icon.component';
import * as Msdyn365 from '@msdyn365-commerce/core';

export interface IHeaderState {
    mobileMenuCollapsed: boolean;
    signinPopoverOpen: boolean;
    hasSigninerror?: boolean;
}

export interface IExtraLinksData {
    linkText?: string;
    linkUrl: Msdyn365.ILinkData;
    ariaLabel?: string;
    openInNewTab?: boolean;
}

export interface IHeaderViewProps extends IHerbalTopHeaderProps<IHerbalTopHeaderData>, IHeaderState {
    wishListIconDesktop: React.ReactNode;
    wishListIconMobile: React.ReactNode;
    cartIcon: React.ReactNode;
    navIcon: React.ReactNode;
    className: string;
    menuBar: React.ReactNode[];
    search: React.ReactNode[];
    siteOptions: React.ReactNode[];
    HeaderTag: IModuleProps;
    HeaderContainer: INodeProps;
    MobileMenuContainer: INodeProps;
    MobileMenuHeader: React.ReactNode;
    MobileMenuBodyContainer: INodeProps;
    MobileMenuLinksContainer: INodeProps;
    HeaderTopBarContainer: INodeProps;
    Divider: INodeProps;

    AccountInfoDropdownParentContainer?: INodeProps;
    AccountInfoDropdownPopoverConentContainer?: INodeProps;
    accountInfoDropdownButton?: React.ReactNode;

    signOutLink?: React.ReactNode;
    signInLink?: React.ReactNode;
    accountLinks?: React.ReactNode[];
    myAccountButton?: React.ReactNode[];
    extraLinks?: JSX.Element;
    preferredStore?: React.ReactNode;
}

/**
 *
 * Header component.
 * @extends {React.PureComponent<IHerbalTopHeaderProps<IHerbalTopHeaderData>>}
 */
@observer
class Header extends React.PureComponent<IHerbalTopHeaderProps<IHerbalTopHeaderData>, IHeaderState> {
    private readonly popOverRef: React.RefObject<HTMLButtonElement>;

    private readonly telemetryContent: ITelemetryContent;

    private cart: ICartState | undefined;
    private originURL: string = '';

    @computed public get displayName(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.FirstName || customer.Name : undefined;
    }

    constructor(props: IHerbalTopHeaderProps<IHerbalTopHeaderData>) {
        super(props);
        this.popOverRef = React.createRef();
        this._toggleNavbar = this._toggleNavbar.bind(this);
        this._togglePopover = this._togglePopover.bind(this);
        this._keydown = this._keydown.bind(this);
        this.state = {
            mobileMenuCollapsed: true,
            signinPopoverOpen: false,
            hasSigninerror: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.addEventListener('keydown', this._keydown);
        }
        reaction(
            () => this.props.data.cart.result,
            () => {
                this.cart = this.props.data.cart.result;
            }
        );
        this.originURL =
            this.props.context.actionContext.requestContext.app.config.ecomBaseUrl &&
            this.props.context.actionContext.requestContext.app.config.ecomBaseUrl;
    }

    public componentWillUnmount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.removeEventListener('keydown', this._keydown, false);
        }
    }

    // eslint-disable-next-line complexity -- Disabled complexity.
    public render(): JSX.Element | null {
        const {
            id,
            typeName,
            data: {
                accountInformation,
                storeSelectorStateManager: { result: storeSelectorStateManager }
            },
            context: {
                request: { user }
            },
            context,
            resources: { cartLabel, cartQtyLabel, signUpCustomerNotFoundTitle, signUpMultipleCustomerFoundTitle, closeNotificationLabel }
        } = this.props;
        const signinNotificationError = this._getSigninNotification(user, signUpMultipleCustomerFoundTitle, signUpCustomerNotFoundTitle);
        if (signinNotificationError !== undefined && this.state.hasSigninerror === false) {
            // For signup show simple error notification.
            NotificationsManager.instance().addNotification(new ErrorNotification(signinNotificationError, closeNotificationLabel));
            this.setState({ hasSigninerror: true });
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- app config is generic
        const disableTooltip = this.props.context.app.config.disableTooltip || false;
        const headerToggleClass = this.state.mobileMenuCollapsed ? '' : 'ms-header-open';
        const headerClassName = classnames('ms-header', this.props.config.className, headerToggleClass);
        const navbarKey = 'header-nav-mobile';
        const customer = accountInformation && accountInformation.result;
        const cartIconSlot = this._getSlot('cartIcon');
        const menuBarSlot = this._getSlot('menuBar');
        const searchSlot = this._getSlot('search');
        const siteOptionsSlot = this._getSlot('siteOptions');
        const preferredStoreSlot = this._getSlot('storeSelector');
        if (!customer) {
            // @ts-ignore
            return null;
        }
        // @ts-ignore
        const viewProps: IHeaderViewProps = {
            ...(this.props as IHerbalTopHeaderProps<IHerbalTopHeaderData>),
            ...this.state,
            wishListIconDesktop: (
                <WishListIconComponent
                    className='ms-header__wishlist-desktop'
                    showButtonTooltip={!disableTooltip}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{}}
                />
            ),
            wishListIconMobile: (
                <WishListIconComponent
                    className='ms-header__wishlist-mobile'
                    showButtonTooltip={!disableTooltip}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{}}
                />
            ),
            cartIcon:
                !customer && cartIconSlot && cartIconSlot.length > 0 ? (
                    cartIconSlot[0]
                ) : (
                    <CartIconComponent
                        cartLabel={cartLabel}
                        cartQtyLabel={cartQtyLabel}
                        context={context}
                        id={id}
                        typeName={typeName}
                        telemetryContent={this.telemetryContent}
                        data={{ cart: this.cart }}
                    />
                ),
            navIcon: (
                <NavIcon
                    {...{
                        resources: this.props.resources,
                        isExpanded: !this.state.mobileMenuCollapsed,
                        targetId: navbarKey,
                        toggleNavBar: this._toggleNavbar,
                        telemetryContent: this.telemetryContent
                    }}
                />
            ),
            menuBar: (menuBarSlot && menuBarSlot.length && menuBarSlot) || [],
            search: (searchSlot && searchSlot.length && searchSlot) || [],
            siteOptions: (siteOptionsSlot && siteOptionsSlot.length && siteOptionsSlot) || [],
            className: headerClassName,
            HeaderTag: {
                moduleProps: this.props,
                className: classnames(headerClassName),
                tag: 'header'
            },
            HeaderContainer: {
                className: classnames('ms-header__container')
            },
            HeaderTopBarContainer: {
                className: classnames('ms-header__topbar')
            },
            Divider: {
                className: classnames('ms-header__divider')
            },
            MobileMenuContainer: {
                tag: Modal,
                id: navbarKey,
                className: 'ms-header__mobile-hamburger',
                isOpen: !this.state.mobileMenuCollapsed,
                wrapClassName: 'ms-header__modal',
                toggle: this._toggleNavbar
            },
            MobileMenuHeader: <ModalHeader className='ms-header__mobile-hamburger-menu-header' toggle={this._toggleNavbar} />,
            MobileMenuBodyContainer: {
                tag: ModalBody,
                className: 'ms-header__mobile-hamburger-menu-body'
            },
            MobileMenuLinksContainer: {
                className: 'ms-header__mobile-hamburger-menu-links'
            },
            AccountInfoDropdownParentContainer: {
                className: 'ms-header__account-info'
            },
            signInLink: this._getSigninButton(customer, user, this.props.resources),
            signOutLink: this._getSignOutButton(customer, user, this.props.resources),
            accountInfoDropdownButton: this._getAccountInfoDropdownButton(customer),
            myAccountButton: this._getMyAccountButton(customer),
            extraLinks: this._getExtraLink(),
            preferredStore: preferredStoreSlot && preferredStoreSlot.length && storeSelectorStateManager && (
                <HeaderPreferredStore {...this.props} />
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /* Handle link text change*/
    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent) => {
        if (this.props.config.myAccountLinks && this.props.config.myAccountLinks[linkIndex]) {
            this.props.config.myAccountLinks[linkIndex].linkText = event.target.value;
        }
    };

    private _getSlot(slotName: string): React.ReactNode[] | null {
        const { slots } = this.props;
        return (slots && slots[slotName] && slots[slotName].length && slots[slotName]) || null;
    }

    private _getSigninButton(
        customer: Customer | undefined,
        user: MsDyn365.IRequestContextUser | undefined,
        resources: IHerbalTopHeaderResources
    ): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignIn);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        const { signInButtonText } = this.props.config;
        const loginCallback = Msdyn365.getUrlSync('logincallback', this.props.context.actionContext);
        const signInUrl =
            user &&
            (loginCallback && loginCallback !== '/logincallback'
                ? `${user.signInUrl}?ru=${this.originURL}${loginCallback}`
                : user.signInUrl);
        return (
            !customer && (
                <Button
                    className='ms-header__signin-button'
                    title={signInButtonText}
                    href={signInUrl}
                    aria-label={resources.signInLinkAriaText}
                    onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Practitioner Login')}
                    {...attributes}
                >
                    <span className='ms-header__signin-button-text' aria-hidden>
                        {signInButtonText}
                    </span>
                </Button>
            )
        );
    }

    private _getSignOutButton(
        customer: Customer | undefined,
        user: MsDyn365.IRequestContextUser | undefined,
        resources: IHerbalTopHeaderResources
    ): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignOut);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        const { signOutButtonText } = this.props.config;
        const loginCallback = Msdyn365.getUrlSync('logincallback', this.props.context.actionContext);
        const signOutUrl =
            user &&
            (loginCallback && loginCallback !== '/logincallback'
                ? `${user.signOutUrl}?ru=${this.originURL}${loginCallback}`
                : user.signOutUrl);
        return (
            customer &&
            this.displayName && (
                <Button
                    className='ms-header__signout-button'
                    title={signOutButtonText}
                    href={signOutUrl || ''}
                    aria-label={resources.signOutLinkAriaText}
                    onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Logout')}
                    {...attributes}
                >
                    <span className='ms-header__signout-button-text' aria-hidden>
                        {signOutButtonText}
                    </span>
                </Button>
            )
        );
    }

    private _getMyAccountButton(customer: Customer | undefined): React.ReactNode[] | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.MyProfile);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        const { myAccountButtonLinks, myAccountButtonImage } = this.props.config;
        if (!customer || !myAccountButtonLinks || myAccountButtonLinks.length === 0) {
            return undefined;
        }
        return myAccountButtonLinks.map((cta: IMyAccountLinksData, index: number) => {
            payLoad.contentAction.etext = cta.linkText;
            const editableLink: MsDyn365.ILinksData = {
                ariaLabel: cta.ariaLabel,
                className: 'ms-signin-info__my-account-button-link',
                linkText: cta.linkText,
                linkUrl: cta.linkUrl.destinationUrl,
                openInNewTab: cta.openInNewTab,
                role: 'button',
                additionalProperties: attributes
            };

            // @ts-ignore
            return (
                // eslint-disable-next-line react/jsx-key
                <Button
                    className={editableLink.className}
                    title={editableLink.linkText}
                    href={editableLink.linkUrl || ''}
                    aria-label={editableLink.linkText}
                    onClick={onTelemetryClick(this.telemetryContent, payLoad, TelemetryConstant.MyProfile)}
                    {...attributes}
                >
                    <img
                        className='ms-signin-info__my-account-button-image'
                        alt={myAccountButtonImage?.altText}
                        src={myAccountButtonImage?.src}
                        height={'30'}
                        width={'30'}
                    />
                </Button>
            );
        });
    }

    private _getExtraLink(): JSX.Element | undefined {
        const { extraLinks } = this.props.config;
        const linkItems =
            extraLinks &&
            extraLinks.map((cta: IExtraLinksData, index: number) => {
                const editableLink: MsDyn365.ILinksData = {
                    ariaLabel: cta.ariaLabel,
                    className: 'ms-nav__list__item__link',
                    linkText: cta.linkText,
                    linkUrl: cta.linkUrl.destinationUrl,
                    openInNewTab: cta.openInNewTab,
                    role: 'link'
                };

                return (
                    <li key={`li-${index}`} className={'ms-nav__list'}>
                        <MsDyn365.Link key={index} link={editableLink} />
                    </li>
                );
            });
        if (linkItems) {
            return <ul className={'ms-nav__list'}>{linkItems}</ul>;
        }
        return undefined;
    }

    private _getSigninNotification(
        user: MsDyn365.IRequestContextUser | undefined,
        signUpMultipleCustomerFoundTitle: string,
        signUpCustomerNotFoundTitle: string
    ): string | undefined {
        const { signUpCustomerNotFound, signUpMultipleCustomerFound } = this.props.config;
        const notFoundError = 'Microsoft_Dynamics_Commerce_Runtime_CustomerNotFound_WhenAutoLinking';
        const multipleAccountError = 'Microsoft_Dynamics_Commerce_Runtime_MultipleCustomerAccountsFoundWithSameEmailAddress';

        if (user?.retailServerErrorCode === notFoundError) {
            return signUpCustomerNotFound !== undefined ? signUpCustomerNotFound : signUpCustomerNotFoundTitle;
        } else if (user?.retailServerErrorCode === multipleAccountError) {
            return signUpMultipleCustomerFound !== undefined ? signUpMultipleCustomerFound : signUpMultipleCustomerFoundTitle;
        }
        return undefined;
    }

    private _getAccountInfoDropdownButton(customer: Customer | undefined): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.MyProfile);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return (
            customer &&
            this.displayName && (
                <Button
                    innerRef={this.popOverRef}
                    className='ms-header__profile-button'
                    aria-describedby='myprofilePopover'
                    onClick={this._togglePopover}
                    color='link'
                    aria-label={this.displayName}
                    aria-expanded={this.state.signinPopoverOpen}
                    {...attributes}
                >
                    <span className='ms-profile-button-text'>{this.displayName}</span>
                </Button>
            )
        );
    }

    private _toggleNavbar(): void {
        this.setState({
            mobileMenuCollapsed: !this.state.mobileMenuCollapsed
        });
    }

    private _togglePopover(): void {
        this.setState({
            signinPopoverOpen: !this.state.signinPopoverOpen
        });
    }

    private _keydown(e: KeyboardEvent): void {
        if (e.keyCode === KeyCodes.Escape) {
            this.setState({ mobileMenuCollapsed: true });
        }
    }
}

export default Header;
